import { useMemo } from "react";
import { resources } from "@constants/hardcoded-strings";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { CustomerSupportTier } from "@idw-types/data/reference-data/generic/customer-support-tier";
import { sortStrings } from "@utils/string-util";
import { toObject } from "@utils/object-util";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { OrganizationTableCell } from "./organization-table-cell/organization-table-cell";
import styles from "./organizations-table-container.less";
import Badge from "react-bootstrap/Badge";
import { ENTITY_STATUS_OPTIONS } from "@constants/entity-status-options";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type OrganizationsTableContainerProps = {
    axnCompanies: Array<AxnCompany>;
    customerSupportTiers: Array<CustomerSupportTier>;
    tableState: TableState;
    isPageHeight: boolean;
};

export type AxnCompanyTableDataRow = AxnCompany & {
    customerSupportTierName: string;
};

export const OrganizationsTableContainer = (props: OrganizationsTableContainerProps) => {
    const { axnCompanies, customerSupportTiers, tableState, isPageHeight } = props;
    const dispatch = useDispatch();

    const filteredAxnCompanies = useMemo(() => {
        return axnCompanies.filter(company => company.type === "RP");
    }, [axnCompanies]);

    const customerSupportTiersMap = useMemo(() => {
        return toObject<CustomerSupportTier>(customerSupportTiers, "uid");
    }, [customerSupportTiers]);

    const organizationsTableData: Array<AxnCompanyTableDataRow> = useMemo(() => {
        return filteredAxnCompanies.map(company => ({
            ...company,
            companyCode: company.companyCode || resources.common.notFound,
            customerSupportTierName: company.customerSupportTierUID ? customerSupportTiersMap[company.customerSupportTierUID].name : null
        }));
    }, [filteredAxnCompanies, customerSupportTiersMap]);

    const customerSupportTierOptions = useMemo(() => {
        return [...customerSupportTiers]
            .sort((a, b) => parseInt(a.id) - parseInt(b.id))
            .map(customerTier => ({ label: customerTier.name, value: customerTier.uid }))
    }, [customerSupportTiers]);

    const columnHelper = createColumnHelper<AxnCompanyTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("name", {
            header: resources.tables.organizations.columns.name.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.name, b.original.name),
            cell: (props) => <OrganizationTableCell data={props.row.original} />
        }),
        columnHelper.accessor("customerSupportTierName", {
            header: resources.tables.organizations.columns.tier.header,
            filterFn: "multiSelectFilter",
            meta: {
                filterOptions: customerSupportTierOptions
            },
        }),
        columnHelper.accessor("companyCode", {
            header: resources.tables.organizations.columns.code.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.companyCode, b.original.companyCode)
        }),
        columnHelper.accessor("type", {
            header: resources.tables.organizations.columns.type.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("status", {
            header: resources.tables.organizations.columns.status.header,
            filterFn: "singleSelectFilter",
            cell: (props) => {
                switch (props.getValue()) {
                    case EntityStatusTypes.ACTIVE:
                        return <Badge bg={"success"}>{resources.common.active}</Badge>;
                    case EntityStatusTypes.INACTIVE:
                        return <Badge bg={"danger"}>{resources.common.inactive}</Badge>;
                    default:
                        return resources.common.notFound;
                }
            },
            meta: {
                filterOptions: ENTITY_STATUS_OPTIONS
            }
        })
    ]), [organizationsTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: organizationsTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.ORGANIZATIONS, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.ORGANIZATIONS, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.ORGANIZATIONS, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="organizations"
                useTableResult={useTableResult}
                className={styles.organizationsTable}
                headerColumnKeyPrefix={"organizations"}
            />
            <PaginationContainer
                idPrefix="organizations"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};